import type { ComponentProps } from 'react';
import {
  Bell,
  BeerStein,
  Broom,
  CalendarBlank,
  ChartBar,
  Clock,
  ClockCounterClockwise,
  Envelope,
  FileText,
  Fire,
  ForkKnife,
  Gauge,
  Ghost,
  Handshake,
  Heartbeat,
  House,
  Info,
  Key,
  Lightning,
  List,
  LockSimple,
  MagnifyingGlass,
  Medal,
  Mountains,
  PencilSimple,
  PersonSimpleRun,
  Plug,
  Plus,
  PlusCircle,
  Prohibit,
  Ranking,
  Scales,
  Shield,
  SoccerBall,
  SquaresFour,
  Sword,
  Target,
  TrendUp,
  Trophy,
  User,
  UsersThree,
  X
} from '@phosphor-icons/react';

/* ==========================================================================
   Iconen — Phosphor.

   Hiervoor waren ze met de hand getekend, en dat werkte prima zolang het er
   negen waren. Toen er prestaties, records en lege schermen bijkwamen werden
   het er dertig, en dan wint een set die af is van een set die van ons is:
   Phosphor tekent alles op hetzelfde raster, in dezelfde dikte.

   De namen blijven Nederlands en veranderen niet mee, zodat de rest van de app
   niet hoeft te weten waar ze vandaan komen. Wil je later een andere set, dan
   is dit bestand het enige dat verandert.

   Alles staat op `regular`: fijner dan de \`bold\` naast Google Sans, en zwaar
   genoeg om op 18 pixels nog te lezen.
   ========================================================================== */

type IcoonProps = ComponentProps<typeof House>;

/** Zelfde maat en uitlijning als voorheen, zodat niets verspringt. */
function stijl({ className = '', ...rest }: IcoonProps): IcoonProps {
  return { weight: 'regular', ...rest, className: `h-[18px] w-[18px] shrink-0 ${className}` };
}

/* ---------- de navigatie ---------- */

export const IcoonHome = (p: IcoonProps) => <House {...stijl(p)} />;
export const IcoonDashboard = (p: IcoonProps) => <SquaresFour {...stijl(p)} />;
export const IcoonNieuw = (p: IcoonProps) => <PlusCircle {...stijl(p)} />;
export const IcoonBeker = (p: IcoonProps) => <Trophy {...stijl(p)} />;
export const IcoonRanglijst = (p: IcoonProps) => <Ranking {...stijl(p)} />;
export const IcoonStatistiek = (p: IcoonProps) => <ChartBar {...stijl(p)} />;
export const IcoonSpelers = (p: IcoonProps) => <UsersThree {...stijl(p)} />;
export const IcoonSpeler = (p: IcoonProps) => <User {...stijl(p)} />;
export const IcoonHistorie = (p: IcoonProps) => <ClockCounterClockwise {...stijl(p)} />;
export const IcoonSleutel = (p: IcoonProps) => <Key {...stijl(p)} />;
export const IcoonStekker = (p: IcoonProps) => <Plug {...stijl(p)} />;
export const IcoonPoule = (p: IcoonProps) => <Target {...stijl(p)} />;
export const IcoonLogs = (p: IcoonProps) => <FileText {...stijl(p)} />;
export const IcoonGebruik = (p: IcoonProps) => <Gauge {...stijl(p)} />;
export const IcoonBel = (p: IcoonProps) => <Bell {...stijl(p)} />;
export const IcoonInfo = (p: IcoonProps) => <Info {...stijl(p)} />;
export const IcoonSlot = (p: IcoonProps) => <LockSimple {...stijl(p)} />;
export const IcoonPlus = (p: IcoonProps) => <Plus {...stijl(p)} />;
export const IcoonMenu = (p: IcoonProps) => <List {...stijl(p)} />;
export const IcoonPotlood = (p: IcoonProps) => <PencilSimple {...stijl(p)} />;
export const IcoonKruis = (p: IcoonProps) => <X {...stijl(p)} />;

/* ---------- opzoeken op naam ----------

   Prestaties, records en lege schermen bewaren een tekstsleutel in plaats van
   JSX; die komt hier uit de kast. Een onbekende naam geeft een blad — beter
   dan een gat in de bladspiegel.
   ------------------------------------- */

const SYMBOLEN = {
  bezem: Broom,
  hart: Heartbeat,
  bord: ForkKnife,
  schild: Shield,
  loper: PersonSimpleRun,
  berg: Mountains,
  duo: UsersThree,
  glas: BeerStein,
  knal: Lightning,
  stijging: TrendUp,
  vlam: Fire,
  kalender: CalendarBlank,
  bal: SoccerBall,
  zoek: MagnifyingGlass,
  handdruk: Handshake,
  weegschaal: Scales,
  zwaarden: Sword,
  medaille: Medal,
  brief: Envelope,
  sleutel: Key,
  geest: Ghost,
  verbod: Prohibit,
  klok: Clock,
  info: Info,
  blad: FileText,
  beker: Trophy,
  poule: Target,
  spelers: UsersThree,
  ranglijst: Ranking,
  statistiek: ChartBar,
  grafiek: TrendUp
} as const;

export type SymboolNaam = keyof typeof SYMBOLEN;

export function Symbool({ naam, ...rest }: { naam: string } & IcoonProps) {
  const Gekozen = SYMBOLEN[naam as SymboolNaam] ?? FileText;
  return <Gekozen {...stijl(rest)} />;
}
